/** Private tab-memory only. Never serialize drafts, attachments or reading state. */
export type ContinuityLease = Readonly<{
  actorEpoch: symbol;
  scope: string;
  binding: string;
  revision: number;
}>;

export type ContinuityEntry<T = unknown> = Readonly<{
  lease: ContinuityLease;
  value: T;
  dirty: boolean;
}>;

export type ContinuitySubmission = Readonly<{ actorEpoch: symbol; scope: string; id: symbol }>;

export class MyCoachContinuityStore {
  private actorEpoch = Symbol('my-coach-actor');
  private revision = 0;
  private active = true;
  private changes = 0;
  private entries = new Map<string, ContinuityEntry>();
  private submissions = new Map<string, ContinuitySubmission>();
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  snapshot<T>(scope: string): ContinuityEntry<T> | undefined {
    return this.active ? this.entries.get(scope) as ContinuityEntry<T> | undefined : undefined;
  }

  version = () => this.changes;

  dirtyLeases(prefix: string): ContinuityLease[] {
    return this.active ? [...this.entries].filter(([key, entry]) => key.startsWith(prefix) && entry.dirty).map(([, entry]) => entry.lease) : [];
  }

  write<T>(scope: string, binding: string, value: T, dirty: boolean): ContinuityLease | undefined {
    if (!this.active) return;
    const lease = { actorEpoch: this.actorEpoch, scope, binding, revision: ++this.revision };
    this.entries.set(scope, { lease, value, dirty });
    this.emit();
    return lease;
  }

  clearIfCurrent = (lease: ContinuityLease | undefined): boolean => {
    if (!lease || !this.active || lease.actorEpoch !== this.actorEpoch) return false;
    const current = this.entries.get(lease.scope);
    if (current?.lease.revision !== lease.revision || current.lease.binding !== lease.binding) return false;
    this.entries.delete(lease.scope);
    this.emit();
    return true;
  };

  markCleanIfCurrent<T>(lease: ContinuityLease | undefined, value: T): boolean {
    if (!lease || !this.active || lease.actorEpoch !== this.actorEpoch) return false;
    const current = this.entries.get(lease.scope);
    if (current?.lease.revision !== lease.revision || current.lease.binding !== lease.binding) return false;
    this.write(lease.scope, lease.binding, value, false);
    return true;
  }

  hasDirty = () => this.active && [...this.entries.values()].some(entry => entry.dirty);

  isSubmitting = (scope: string) => this.active && this.submissions.has(scope);

  beginSubmission(scope: string, binding: string): ContinuitySubmission | undefined {
    const entry = this.entries.get(scope);
    if (!this.active || this.submissions.has(scope) || (entry?.dirty && entry.lease.binding !== binding)) return;
    const ticket = { actorEpoch: this.actorEpoch, scope, id: Symbol('submission') };
    this.submissions.set(scope, ticket); this.emit(); return ticket;
  }

  endSubmission = (ticket: ContinuitySubmission | undefined) => {
    if (!ticket || !this.active || ticket.actorEpoch !== this.actorEpoch || this.submissions.get(ticket.scope) !== ticket) return;
    this.submissions.delete(ticket.scope); this.emit();
  };

  isCurrentSubmission = (ticket: ContinuitySubmission | undefined) => Boolean(ticket && this.active
    && ticket.actorEpoch === this.actorEpoch && this.submissions.get(ticket.scope) === ticket);

  revoke = () => {
    this.active = false;
    this.entries.clear();
    this.submissions.clear();
    this.emit();
  };

  activate = () => {
    if (this.active) return;
    this.actorEpoch = Symbol('my-coach-actor');
    this.active = true;
    this.emit();
  };

  private emit() { this.changes += 1; for (const listener of this.listeners) listener(); }
}
